<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      loading: true,
      tableColumns: [
        { key: 'employee', label: 'Employee' },
        { key: 'structure', label: 'Structure' },
        { key: 'ctc', label: 'CTC (Annual / Monthly)' },
        { key: 'effective', label: 'Effective Period' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', label: 'Actions', width: '80px' },
      ],
      salaries: [],
      search: '',
      pagination: {
        current_page: 1,
        per_page: 30,
        last_page: 1,
        total: 0,
        links: [],
      },
    };
  },
  mounted() {
    this.fetchSalaries();
  },
  methods: {
    async fetchSalaries(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/employee-salaries', {
          params: { page },
        });
        this.salaries = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          links: response.data.data.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    money(val) {
      const num = parseFloat(val);
      if (isNaN(num)) return '-';
      return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    },

    openAssign() {
      this.$refs.salaryModal.assign();
      this.$openModal('hrm_employee_salary_modal');
    },

    openRevise(row) {
      this.$refs.salaryModal.revise(row);
      this.$openModal('hrm_employee_salary_modal');
    },

    openHistory(row) {
      this.$refs.historyModal.open({
        id: row.employee_id,
        name: row.employee?.full_name || '-',
        code: row.employee?.code || '',
      });
      this.$openModal('hrm_employee_salary_history_modal');
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-flex align-items-center justify-content-between page-breadcrumb mb-3">
        <div>
          <h4 class="page-title mb-0">Employee Salaries</h4>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="openAssign">
            <i class="ti ti-circle-plus me-2"></i>Assign Salary
          </a>
        </div>
      </div>

      <!-- Main Card -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">

        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Salary Records
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>
        </div>

        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">

          <app-table
            :columns="tableColumns"
            :rows="salaries"
            :pagination="pagination"
            :loading="loading"
            empty-text="No salary records yet."
            empty-icon="ti ti-cash"
            @page-change="fetchSalaries"
          >
            <!-- Employee -->
            <template #employee="{ row }">
              <div class="d-flex flex-column">
                <span style="font-size:12.5px; font-weight:600; color:#1e293b;">{{ row.employee?.full_name || '-' }}</span>
                <span v-if="row.employee?.code" class="es-code">{{ row.employee.code }}</span>
              </div>
            </template>

            <!-- Structure -->
            <template #structure="{ row }">
              <span class="es-struct">{{ row.salary_structure?.name || '-' }}</span>
            </template>

            <!-- CTC -->
            <template #ctc="{ row }">
              <span style="font-size:13px; font-weight:700; color:#0f172a;">{{ money(row.ctc_annual) }}</span>
              <span style="font-size:11px; color:#94a3b8;"> / {{ money(row.ctc_monthly) }} mo</span>
            </template>

            <!-- Effective -->
            <template #effective="{ row }">
              <span style="font-size:11.5px; color:#475569;">
                {{ $formatDate(row.effective_from) }}
                <template v-if="row.effective_to"> — {{ $formatDate(row.effective_to) }}</template>
                <template v-else> <span style="color:#16a34a; font-weight:600;">(ongoing)</span></template>
              </span>
            </template>

            <!-- Status -->
            <template #is_active="{ row }">
              <span :class="row.is_active ? 'es-badge es-active' : 'es-badge es-inactive'">
                {{ row.is_active ? 'Active' : 'Closed' }}
              </span>
            </template>

            <!-- Actions -->
            <template #actions="{ row }">
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openRevise(row)">
                      <i class="ti ti-refresh me-1"></i>Revise Salary
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openHistory(row)">
                      <i class="ti ti-history me-1"></i>View History
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </app-table>

        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-employee-salary-modal ref="salaryModal" @updated="fetchSalaries" />
  <hrm-employee-salary-history-modal ref="historyModal" />
</template>

<style scoped>
.es-code {
  font-size: 10px; font-weight: 700; letter-spacing: .4px;
  color: #64748b; margin-top: 1px;
}
.es-struct {
  font-size: 11.5px; font-weight: 600;
  padding: 3px 10px; border-radius: 20px;
  background: #eef2ff; color: #4f46e5;
}
.es-badge {
  display: inline-block; font-size: 10px; font-weight: 600;
  padding: 3px 10px; border-radius: 20px;
}
.es-active { background: #dcfce7; color: #16a34a; }
.es-inactive { background: #f1f5f9; color: #94a3b8; }
</style>